import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Plan/spec text is extracted IN THE BROWSER with pdf.js, then only the text is
// sent to the API for the AI to read. Scanned/raster sheets have no text layer —
// for those we fall back to OCR (Tesseract.js, lazy-loaded). The AI never counts.
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface PageText { page: number; text: string; ocr?: boolean }
export type ExtractStage = 'text' | 'ocr';
export type ProgressFn = (done: number, total: number, stage: ExtractStage) => void;

// Below this many characters a page is treated as scanned/raster and sent to OCR.
const OCR_MIN_CHARS = 24;
// Render scale for OCR — higher = better recognition, slower.
const OCR_SCALE = 2;

export async function extractPdfPages(
  file: File,
  onProgress?: ProgressFn,
  opts?: { ocr?: boolean },
): Promise<PageText[]> {
  const ocrEnabled = opts?.ocr ?? true;
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  const pages: PageText[] = [];

  // A single OCR worker is created lazily on the first scanned page and reused.
  let ocr: OcrWorker | null = null;
  try {
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      let text = content.items
        .map((it) => ('str' in it ? (it as { str: string }).str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      let usedOcr = false;

      if (ocrEnabled && text.length < OCR_MIN_CHARS) {
        onProgress?.(i, doc.numPages, 'ocr');
        ocr ??= await createOcr();
        text = await ocrPage(ocr, page);
        usedOcr = true;
      } else {
        onProgress?.(i, doc.numPages, 'text');
      }

      pages.push({ page: i, text, ocr: usedOcr });
    }
  } finally {
    await ocr?.terminate();
  }
  return pages;
}

// ── OCR (lazy) ───────────────────────────────────────────────────────────────
interface OcrWorker { recognize(c: HTMLCanvasElement): Promise<{ data: { text: string } }>; terminate(): Promise<unknown>; }

async function createOcr(): Promise<OcrWorker> {
  const { createWorker } = await import('tesseract.js');
  return (await createWorker('eng')) as unknown as OcrWorker;
}

async function ocrPage(worker: OcrWorker, page: pdfjs.PDFPageProxy): Promise<string> {
  const viewport = page.getViewport({ scale: OCR_SCALE });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  await page.render({ canvasContext: ctx, viewport }).promise;
  const { data } = await worker.recognize(canvas);
  return (data.text ?? '').replace(/\s+/g, ' ').trim();
}
